import { EmptySpaceCalculation } from './types'

/**
 * Calculates the empty space in the aspect ratio container when an image is scaled using a "contain" strategy.
 *
 * @param containerWidth - The width of the aspect ratio container in display pixels.
 * @param containerHeight - The height of the aspect ratio container in display pixels.
 * @param imageWidth - The original width of the image (in pixels).
 * @param imageHeight - The original height of the image (in pixels).
 * @param scale - The current scale factor of the image (from transform).
 * @param position - The current position offset of the image {x, y}.
 * @returns an object containing the scaled image dimensions and empty space for each direction.
 */
export const calculateEmptySpace = (
	containerWidth: number,
	containerHeight: number,
	imageWidth: number,
	imageHeight: number,
	scale = 1,
	position = { x: 0, y: 0 },
): EmptySpaceCalculation => {
	// Define a small tolerance for aspect ratio comparison
	const aspectRatioTolerance = 0.005

	// Round all input values once at the start
	const roundedValues = {
		containerWidth: Math.round(containerWidth),
		containerHeight: Math.round(containerHeight),
		imageWidth: Math.round(imageWidth),
		imageHeight: Math.round(imageHeight),
		scale, // Keep scale as float for precise calculations
		position: {
			x: Math.round(position.x),
			y: Math.round(position.y),
		},
	}

	const imageAspect = roundedValues.imageWidth / roundedValues.imageHeight
	const containerAspect = roundedValues.containerWidth / roundedValues.containerHeight

	// Check if the aspect ratios are close enough to be considered the same
	const aspectRatiosMatch = Math.abs(imageAspect - containerAspect) < aspectRatioTolerance

	// Calculate base scaled dimensions
	let baseScaledWidth: number
	let baseScaledHeight: number

	// Only apply the aspect ratio matching for display purposes
	// Don't change the actual calculations to ensure we preserve negative values
	if (containerAspect > imageAspect) {
		// Container is wider relative to the image
		baseScaledHeight = roundedValues.containerHeight
		baseScaledWidth = Math.round(roundedValues.containerHeight * imageAspect)
	} else {
		// Container is taller relative to the image
		baseScaledWidth = roundedValues.containerWidth
		baseScaledHeight = Math.round(roundedValues.containerWidth / imageAspect)
	}

	// Apply scale factor and round the result once
	const scaledImageWidth = Math.round(baseScaledWidth * roundedValues.scale)
	const scaledImageHeight = Math.round(baseScaledHeight * roundedValues.scale)

	// Calculate empty spaces using integer arithmetic
	const totalHorizontalSpace = roundedValues.containerWidth - scaledImageWidth
	const totalVerticalSpace = roundedValues.containerHeight - scaledImageHeight

	// Divide spaces by 2 and round once
	const baseEmptyLeft = Math.round(totalHorizontalSpace / 2)
	const baseEmptyRight = totalHorizontalSpace - baseEmptyLeft // Use integer arithmetic
	const baseEmptyUp = Math.round(totalVerticalSpace / 2)
	const baseEmptyDown = totalVerticalSpace - baseEmptyUp // Use integer arithmetic

	// Calculate final empty spaces using integer arithmetic
	const emptyLeft = baseEmptyLeft + roundedValues.position.x
	const emptyRight = baseEmptyRight - roundedValues.position.x
	const emptyUp = baseEmptyUp + roundedValues.position.y
	const emptyDown = baseEmptyDown - roundedValues.position.y

	// Calculate scale factors once
	const horizontalScaleFactor = roundedValues.imageWidth / scaledImageWidth
	const verticalScaleFactor = roundedValues.imageHeight / scaledImageHeight

	// Convert to original resolution and round once
	const originalEmptyLeft = Math.round(emptyLeft * horizontalScaleFactor)
	const originalEmptyRight = Math.round(emptyRight * horizontalScaleFactor)
	const originalEmptyUp = Math.round(emptyUp * verticalScaleFactor)
	const originalEmptyDown = Math.round(emptyDown * verticalScaleFactor)

	// Only zero out small positive empty spaces when aspect ratios match
	// Never zero out negative values as they're needed for cropping
	const originalEmptyUpFinal = aspectRatiosMatch && originalEmptyUp > 0 && originalEmptyUp <= 3 ? 0 : originalEmptyUp
	const originalEmptyRightFinal =
		aspectRatiosMatch && originalEmptyRight > 0 && originalEmptyRight <= 3 ? 0 : originalEmptyRight
	const originalEmptyDownFinal =
		aspectRatiosMatch && originalEmptyDown > 0 && originalEmptyDown <= 3 ? 0 : originalEmptyDown
	const originalEmptyLeftFinal =
		aspectRatiosMatch && originalEmptyLeft > 0 && originalEmptyLeft <= 3 ? 0 : originalEmptyLeft

	return {
		scaledImageWidth,
		scaledImageHeight,
		emptyLeft,
		emptyRight,
		emptyUp,
		emptyDown,
		originalEmptyLeft: originalEmptyLeftFinal,
		originalEmptyRight: originalEmptyRightFinal,
		originalEmptyUp: originalEmptyUpFinal,
		originalEmptyDown: originalEmptyDownFinal,
		emptyHorizontal: Math.abs(emptyLeft) + Math.abs(emptyRight),
		emptyVertical: Math.abs(emptyUp) + Math.abs(emptyDown),
		originalEmptyHorizontal: Math.abs(originalEmptyLeftFinal) + Math.abs(originalEmptyRightFinal),
		originalEmptyVertical: Math.abs(originalEmptyUpFinal) + Math.abs(originalEmptyDownFinal),
	}
}
